import * as React from 'react';
import { Link, NavLink } from 'react-router-dom';
import Tag from 'antd/lib/tag';

import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';

import ImageWithSkeleton from '@commonTuna/react/components/UI/ImageWithSkeleton/ImageWithSkeleton';
import { numberWithComma } from '@commonTuna/react/utils/NumberWithComma/NumberWithComma';

import { Location } from '@app/objects/Location';
import { getLocationName } from '@app/components/Utils';
import SpecialtyTag from '@app/components/Pages/Specialties/SpecialtyTag';
import Stars from '@app/components/UI/Stars/Stars';

import noImage from '@images/no-image.jpg';

import '@app/scss/pages/clinicsPage.scss';

export const getClinicAvatar = (location: Location, addRemote: boolean = true): string => {
	if (location.portalOriginalAvatar) {
		return addRemote ? `/remote/${location.portalOriginalAvatar}` : location.portalOriginalAvatar;
	}
	if (location.portalAvatar) {
		return addRemote ? `/remote/${location.portalAvatar}` : location.portalAvatar;
	}
	return noImage;
};

const Clinics: React.FC = () => {
	return <ServerPageProvider
		defaultTitle="Clinics"
		inner={(serverPage) =>
			<div className="clinics-page container">
				<BreadCrumbs
					title="Clinics"
				/>
				{serverPage.items && serverPage.items.list.map((location: Location) => {
					if (!location.nameEn || location.nameEn === ' ') {
						return null;
					}

					const title = (<h1>
						<div className="pull-right rate hide-mobile">
							<strong className="mr10">
								<Stars value={location.averageReviewRate < 1 ? 1 : location.averageReviewRate} />
							</strong>
							{numberWithComma(location.averageReviewRate)}
							{' '}
							(
							{location.totalReviewCount}
							)
						</div>
						{getLocationName(location, false, true, true)}
						<div className="pull-right rate show-mobile">
							<strong className="mr10">
								<Stars value={location.averageReviewRate < 1 ? 1 : location.averageReviewRate} />
							</strong>
							{numberWithComma(location.averageReviewRate)}
							{' '}
							(
							{location.totalReviewCount}
							)
						</div>
					</h1>);

					return <div className="clinic-card clearfix" key={location.id}>
						{location.path
							? <Link to={`/clinics/${location.path}`} className="clinic-card__image">
								<ImageWithSkeleton
									src={getClinicAvatar(location)}
									alt="Clinic avatar"
								/>
							</Link>
							: <div className="clinic-card__image">
								<ImageWithSkeleton
									src={getClinicAvatar(location)}
									alt="Clinic avatar"
								/>
							</div>
						}
						<div className="clinic-card__body">
							<div>
								{location.portalPathEn
									? <Link to={`/clinics/${location.portalPathEn}`} className="clinic-card__name">
										<h2>
											{getLocationName(location, false, false, true)}
										</h2>
									</Link>
									: <h2>
										{getLocationName(location, false, false, true)}
									</h2>
								}
							</div>
							<div className="clinic-card__tags">
								{location.specialties?.length > 0 && location.specialties.filter((item) => item)
									.map((item) => <SpecialtyTag
										key={`specialty${item.id}`}
										tagClassName="title__second"
										specialty={item}
									/>)}
								<div className="space" key="space" />
								{location.procedures?.length > 0 && location.procedures?.filter((item) => item)
									.map((item) =>
										(<span key={`procedure${item.id}`}>
											{item.path
												? <NavLink to={`/procedures/${item.path}`}>
													<Tag color="purple" key={item.id} className="title__second">
														{item.name}
													</Tag>
												</NavLink>
												: <Tag color="purple" key={item.id} className="title__second">
													{item.name}
												</Tag>
											}
										</span>))}
							</div>
							{location.portalShortDescription
								&& <div
									className="clinic-card__description mt10"
									dangerouslySetInnerHTML={{ __html: location.portalShortDescription }}
								/>
							}
						</div>
					</div>;
				})}
			</div>
		}
	/>;
};

export default Clinics;
